// Global
import React, { useContext } from 'react';

// Local
import clsx from 'clsx';
import CardListingStyles from '../CardListing.module.scss';
import { useContentGridSettings } from 'lib/hooks/useSiteSettings';
import styles from '../ThreePackCards/ThreePackCards.module.scss';
import {
  CardFields,
  getImageSettingsByContentType,
  normalizeStandardImageUrl,
} from '../CardListingShared';
import { ContentGridCardAlignment, ContentGridProps } from '../ContentGrid/ContentGrid';
import { ContentGridCard } from '../ContentGrid/ContentGridCard';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { SearchRecord } from 'lib/services/models/search-response';
import { SideBySideContext } from '../SideBySideContainer/SideBySideContainer'; // Import the context

export interface ThreePackCardsProps extends Omit<ContentGridProps, 'cards' | 'columns'> {
  cards: SearchRecord[];
  contentOverride?: ContentGridProps['contentOverrides'];
  fallbackImageUrl?: string;
  fieldsToShow: CardFields[];
  additionalCssClassName?: 'threePackCardsCard';
  cardAlignment?: ContentGridCardAlignment;
}

export const ThreePackCards: React.FC<ThreePackCardsProps> = (props) => {
  const isInSideBySideContainer = useContext(SideBySideContext); // Consume the context

  const cards = props.cards || [];
  const fieldsToShow = props.fieldsToShow || (['Image', 'Eyebrow', 'Title'] as CardFields[]);
  // Restrict to first 3 cards
  const limitedCards = cards?.slice(0, 3);
  const additionalCssClassName = props.additionalCssClassName || '';

  const contentGridSettings = useContentGridSettings();
  const defaultAspectRatio = getEnumValue<ImageAspectRatio>(
    contentGridSettings?.fields?.defaultCardImageAspectRatio
  );

  return (
    <div
      className={clsx(
        styles.threePackCardsWrapper,
        isInSideBySideContainer ? '' : styles.wrapperInMainLayout
      )}
    >
      {props.headline && (
        <h2 className={clsx(styles.threePackCardsHeadlineText)}>{props.headline}</h2>
      )}
      <div
        className={clsx(CardListingStyles.contentGrid, CardListingStyles.threePackCardsGrid)}
        data-component="authorable/shared/card-listing/content-grid/content-grid"
      >
        <ul
          className={clsx(
            CardListingStyles.contentGridContent,
            CardListingStyles['col-1'],
            CardListingStyles.threePackCardsGridContent,
            isInSideBySideContainer ? '' : CardListingStyles.containerInMainLayout
          )}
        >
          {limitedCards.map((card) => {
            const override = props.contentOverrides[card.ContentId];
            const { aspectRatio, forceWhiteBackground } = getImageSettingsByContentType(
              contentGridSettings,
              card.ContentType
            );
            card.StandardImageUrl = normalizeStandardImageUrl(card.StandardImageUrl, card.PageUrl);
            return (
              <li
                key={card.ContentId}
                className={clsx(
                  CardListingStyles.contentGridCard,
                  CardListingStyles['col-1'],
                  CardListingStyles.threePackCardsContentGridCard,
                  isInSideBySideContainer ? '' : CardListingStyles.inMainLayout
                )}
              >
                <ContentGridCard
                  card={card}
                  alignment={props.cardAlignment}
                  contentOverride={override}
                  fallbackImageUrl={props.fallbackImageUrl}
                  fieldsToShow={fieldsToShow}
                  gridAspectRatio={props.gridAspectRatio ?? defaultAspectRatio}
                  aspectRatio={aspectRatio}
                  forceWhiteBackground={forceWhiteBackground}
                  additionalCssClassName={additionalCssClassName}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
